import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { toast } from 'sonner';
import { DashboardLayout } from './Dashboard';
import Tour from '../components/Tour';
import NotionExportButton from '../components/NotionExportButton';
import TrelloExportButton from '../components/TrelloExportButton';
import ClickUpExportButton from '../components/ClickUpExportButton';
import {
  Compass, Sparkle, ChartLineUp, Fire, Lightning, Target, Trophy, Users, Trash,
  ArrowRight, Download, Copy, MagnifyingGlass, TrendUp,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'pt', label: 'Português (PT + BR)' },
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' },
];

const MARKETS = [
  { code: '', key: 'none' },
  { code: 'Portugal', key: 'portugal' },
  { code: 'Brasil', key: 'brasil' },
  { code: 'Espanha', key: 'espanha' },
  { code: 'EUA', key: 'eua' },
  { code: 'Global', key: 'global' },
];

const SATURATION_COLOR = { low: '#10B981', medium: '#F59E0B', high: '#EF4444' };
const URGENCY_COLOR = { high: '#EF4444', medium: '#F59E0B', low: '#A0A0A0' };

const copyToClipboard = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label || i18n.t('common.copied'));
  } catch {
    toast.error(i18n.t('common.copy_fail'));
  }
};

const downloadJson = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const ScoreBar = ({ value, color = '#10B981' }) => {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${v}%`, background: color }} />
      </div>
      <span className="text-xs font-bold w-8 text-right" style={{ color }}>{v}</span>
    </div>
  );
};

const SectionCard = ({ icon: Icon, title, color = '#A855F7', children, actions }) => (
  <div className="p-5 bg-[#121212] border border-white/10 rounded-2xl">
    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}22` }}>
          <Icon size={16} weight="bold" style={{ color }} />
        </div>
        <h3 className="font-[Outfit] font-bold">{title}</h3>
      </div>
      {actions}
    </div>
    {children}
  </div>
);

const SignalsView = ({ s }) => {
  const { t } = useTranslation();
  if (!s) return null;
  const satColor = SATURATION_COLOR[s.saturation_label] || '#A0A0A0';
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          { label: t('opps.saturation'), value: s.saturation_label, color: satColor, big: true, score: s.saturation_score },
        ].map((c, i) => (
          <div key={i} className="p-3 bg-white/5 border border-white/10 rounded-lg col-span-2 md:col-span-4">
            <div className="text-[10px] uppercase tracking-wider text-white/40">{c.label}</div>
            <div className="text-xl font-[Outfit] font-bold capitalize" style={{ color: c.color }}>{c.value}</div>
            {typeof c.score === 'number' && <ScoreBar value={c.score} color={c.color} />}
          </div>
        ))}
      </div>
      {[
        { k: 'search_momentum', label: t('opps.search_momentum'), icon: MagnifyingGlass },
        { k: 'social_buzz', label: t('opps.social_buzz'), icon: Fire },
        { k: 'spending_signals', label: t('opps.spending_signals'), icon: TrendUp },
      ].map(({ k, label, icon: Ic }) => s[k] && (
        <div key={k} className="p-3 bg-white/5 border-l-2 border-[#A855F7] rounded-r-lg flex items-start gap-2">
          <Ic size={14} weight="bold" className="text-[#A855F7] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
            <p className="text-sm text-white/85">{s[k]}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const SubNicheCard = ({ n, onClone }) => {
  const { t } = useTranslation();
  const score = n.opportunity_score || 0;
  const color = score >= 75 ? '#10B981' : score >= 50 ? '#F59E0B' : '#A0A0A0';
  const fma = { high: '#10B981', medium: '#F59E0B', low: '#A0A0A0' }[n.first_move_advantage] || '#A0A0A0';
  const price = n.estimated_price_eur || {};
  return (
    <div className="p-4 bg-[#0A0A0A] border border-white/10 rounded-xl">
      <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="font-[Outfit] font-bold text-base mb-1">{n.name}</div>
          <p className="text-xs text-white/60">{n.audience_one_liner}</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-white/40">Score</div>
          <div className="text-2xl font-[Outfit] font-bold" style={{ color }}>{score}</div>
        </div>
      </div>
      <ScoreBar value={score} color={color} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
        {n.why_now && (
          <div className="p-2 bg-[#2563EB]/10 border border-[#2563EB]/25 rounded text-xs">
            <div className="text-[10px] uppercase tracking-wider text-[#2563EB] font-semibold mb-0.5">{t('opps.why_now')}</div>
            <p className="text-white/80">{n.why_now}</p>
          </div>
        )}
        {n.underserved_angle && (
          <div className="p-2 bg-[#10B981]/10 border border-[#10B981]/25 rounded text-xs">
            <div className="text-[10px] uppercase tracking-wider text-[#10B981] font-semibold mb-0.5">{t('opps.underserved')}</div>
            <p className="text-white/80">{n.underserved_angle}</p>
          </div>
        )}
        {n.product_idea && (
          <div className="p-2 bg-[#A855F7]/10 border border-[#A855F7]/25 rounded text-xs md:col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-[#A855F7] font-semibold mb-0.5">{t('opps.product_idea')}</div>
            <p className="text-white/85">{n.product_idea}</p>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-xs">
          {(price.min || price.recommended) && (
            <span className="px-2 py-1 bg-white/5 border border-white/10 rounded">
              <span className="text-white/50">{t('opps.price')}</span> <strong>€{price.min || '?'}-€{price.recommended || price.min}</strong>
            </span>
          )}
          {n.first_move_advantage && (
            <span className="px-2 py-1 bg-white/5 border border-white/10 rounded">
              <span className="text-white/50">First-mover:</span> <strong style={{ color: fma }} className="capitalize">{n.first_move_advantage}</strong>
            </span>
          )}
        </div>
        <button data-testid={`clone-niche-${n.name?.slice(0, 12)}`}
          onClick={() => onClone?.(n)}
          className="px-3 py-1.5 bg-[#2563EB]/20 border border-[#2563EB]/40 hover:bg-[#2563EB]/30 rounded-lg text-xs font-semibold flex items-center gap-1.5">
          {t('opps.generate_product')} <ArrowRight size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
};

export default function Opportunities() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ seed_niche: '', language: 'pt', target_market: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/opportunities/history`);
      setHistory(data.items || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    const h = () => loadHistory();
    window.addEventListener('brand:changed', h);
    return () => window.removeEventListener('brand:changed', h);
  }, [loadHistory]);

  const onScan = async (e) => {
    e?.preventDefault?.();
    if (!form.seed_niche.trim()) {
      toast.error(t('opps.err_seed'));
      return;
    }
    const startedAt = Date.now();
    setLoading(true);
    setResult(null);
    try {
      const payload = { ...form, target_market: form.target_market || null };
      const { data } = await axios.post(`${BACKEND_URL}/api/opportunities/scan`, payload, { timeout: 120000 });
      setResult(data);
      toast.success(t('opps.scan_done'));
      loadHistory();
    } catch (err) {
      const status = err.response?.status;
      const isTimeout = err.code === 'ECONNABORTED' || status === 502 || status === 504;
      // Fallback: backend may have persisted the doc even if the ingress dropped the response.
      // Poll history for a NEW scan matching this seed_niche created after `startedAt`.
      if (isTimeout) {
        toast.info(t('opps.timeout_info'));
        const recovered = await _pollForRecentScan(form.seed_niche, startedAt);
        if (recovered) {
          setResult(recovered);
          toast.success(t('opps.scan_recovered'));
          loadHistory();
          setLoading(false);
          return;
        }
      }
      if (status === 429) {
        toast.error(err.response?.data?.detail || 'Limite atingido');
        setTimeout(() => navigate('/pricing?utm_source=opportunities_quota'), 1500);
      } else {
        toast.error(err.response?.data?.detail || 'Falha no scan — tenta novamente');
      }
    } finally {
      setLoading(false);
    }
  };

  // Polls history for ~30s looking for a scan with matching seed_niche created after startedAt.
  const _pollForRecentScan = async (seed, startedAt) => {
    for (let attempt = 0; attempt < 10; attempt++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/opportunities/history`);
        const items = data.items || [];
        const match = items.find((x) => x.seed_niche === seed && new Date(x.created_at).getTime() >= startedAt - 2000);
        if (match) {
          const detail = await axios.get(`${BACKEND_URL}/api/opportunities/${match.scan_id}`);
          return detail.data;
        }
      } catch { /* swallow & retry */ }
    }
    return null;
  };

  const loadFromHistory = async (id) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/opportunities/${id}`);
      setResult(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      toast.error(t('opps.load_failed'));
    }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/opportunities/${id}`);
      setHistory((h) => h.filter((x) => x.scan_id !== id));
      if (result?.scan_id === id) setResult(null);
      toast.success(t('opps.deleted'));
    } catch {
      toast.error(t('opps.delete_failed'));
    }
  };

  const cloneToProductGenerator = (subNiche) => {
    // Pre-fill Product Generator with this sub-niche
    const niche = subNiche?.name || result?.seed_niche || '';
    const audience = subNiche?.audience_one_liner || '';
    const params = new URLSearchParams({ niche, audience }).toString();
    navigate(`/product-generator?${params}`);
  };

  const scan = result?.scan || {};

  return (
    <DashboardLayout>
      <Tour tourId="opportunities-v1" steps={[
        { target: '[data-testid="input-seed-niche"]', content: t('opps.tour_1'), disableBeacon: true },
        { target: '[data-testid="select-market"]', content: t('opps.tour_2') },
        { target: '[data-testid="btn-scan"]', content: t('opps.tour_3') },
      ]} />
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-[Outfit] font-bold tracking-tight">Opportunity Finder</h1>
          <p className="text-white/60 text-sm mt-1">{t('opps.subtitle')}</p>
        </div>

        {/* Form */}
        <form onSubmit={onScan} className="p-5 bg-[#121212] border border-white/10 rounded-2xl space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-white/50">{t('opps.seed_label')}</label>
              <input data-testid="input-seed-niche" value={form.seed_niche}
                onChange={(e) => setForm({ ...form, seed_niche: e.target.value })}
                placeholder={t('opps.seed_ph')}
                className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#A855F7]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/50">{t('opps.language')}</label>
              <select data-testid="select-language" value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#A855F7]">
                {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/50">{t('opps.market_label')}</label>
              <select data-testid="select-market" value={form.target_market}
                onChange={(e) => setForm({ ...form, target_market: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#A855F7]">
                {MARKETS.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
              </select>
            </div>
          </div>
          <button data-testid="btn-scan" type="submit" disabled={loading}
            className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-[#A855F7] to-[#2563EB] rounded-xl font-semibold text-sm hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <><Sparkle size={16} weight="bold" className="animate-pulse" /> {t('opps.scanning')}</> : <><Compass size={16} weight="bold" /> {t('opps.scan_btn')}</>}
          </button>
        </form>

        {/* Result */}
        {result && (
          <div className="space-y-4" data-testid="opportunities-result">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Scan · {result.seed_niche}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <button data-testid="btn-export-json"
                  onClick={() => downloadJson(result, `opportunities-${result.scan_id}.json`)}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs font-semibold hover:bg-white/10 flex items-center gap-1.5">
                  <Download size={12} weight="bold" /> JSON
                </button>
                <NotionExportButton
                  shape_type="opportunity_scan"
                  source_id={result.scan_id}
                  testId="btn-export-notion-opportunities"
                />
                <TrelloExportButton
                  shape_type="opportunity_scan"
                  source_id={result.scan_id}
                  testId="btn-export-trello-opportunities"
                />
                <ClickUpExportButton
                  shape_type="opportunity_scan"
                  source_id={result.scan_id}
                  testId="btn-export-clickup-opportunities"
                />
              </div>
            </div>

            {scan.executive_summary && (
              <div className="p-4 bg-gradient-to-br from-[#A855F7]/12 to-transparent border border-[#A855F7]/35 rounded-2xl">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#A855F7] font-semibold mb-2">{t('opps.exec_summary')}</div>
                <p className="text-white/90 leading-relaxed">{scan.executive_summary}</p>
              </div>
            )}

            <SectionCard icon={ChartLineUp} title={t('opps.market_signals')} color="#2563EB">
              <SignalsView s={scan.market_signals} />
            </SectionCard>

            {Array.isArray(scan.sub_niches) && scan.sub_niches.length > 0 && (
              <SectionCard icon={Target} title={t('opps.sub_niches')} color="#10B981">
                <div className="space-y-3">
                  {scan.sub_niches.map((n, i) => <SubNicheCard key={i} n={n} onClone={cloneToProductGenerator} />)}
                </div>
              </SectionCard>
            )}

            {Array.isArray(scan.trending_pain_points) && scan.trending_pain_points.length > 0 && (
              <SectionCard icon={Fire} title={t('opps.pain_points')} color="#EF4444">
                <div className="space-y-2">
                  {scan.trending_pain_points.map((p, i) => (
                    <div key={i} className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg">
                      <div className="flex items-start gap-2">
                        <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold flex-shrink-0"
                          style={{ color: URGENCY_COLOR[p.urgency] || '#A0A0A0', background: `${URGENCY_COLOR[p.urgency] || '#A0A0A0'}22` }}>
                          {p.urgency}
                        </span>
                        <div className="flex-1">
                          <div className="text-sm font-semibold mb-1">{p.pain}</div>
                          {p.evidence && <p className="text-xs text-white/55">{p.evidence}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {Array.isArray(scan.competitor_gaps) && scan.competitor_gaps.length > 0 && (
              <SectionCard icon={Users} title={t('opps.competitor_gaps')} color="#F59E0B">
                <div className="space-y-2">
                  {scan.competitor_gaps.map((g, i) => (
                    <div key={i} className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg">
                      <div className="font-[Outfit] font-bold text-sm mb-1">{g.competitor_archetype}</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                        <div className="text-white/70"><span className="text-[#10B981] font-semibold">{t('opps.strength')}</span> {g.what_they_do_well}</div>
                        <div className="text-white/70"><span className="text-[#EF4444] font-semibold">{t('opps.gap')}</span> {g.gap_they_miss}</div>
                      </div>
                      {g.how_to_attack && (
                        <div className="mt-2 p-2 bg-[#A855F7]/10 border border-[#A855F7]/25 rounded text-xs">
                          <span className="text-[#A855F7] font-semibold uppercase tracking-wider text-[10px]">{t('opps.how_attack')} </span>
                          <span className="text-white/80">{g.how_to_attack}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {Array.isArray(scan.quick_wins) && scan.quick_wins.length > 0 && (
              <SectionCard icon={Lightning} title={t('opps.quick_wins')} color="#10B981">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {scan.quick_wins.map((q, i) => (
                    <div key={i} className="p-3 bg-[#10B981]/8 border border-[#10B981]/25 rounded-lg text-sm flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-[#10B981]/20 flex items-center justify-center text-[10px] font-bold text-[#10B981] flex-shrink-0">{i + 1}</span>
                      <span className="text-white/85">{q}</span>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {Array.isArray(scan.research_hooks) && scan.research_hooks.length > 0 && (
              <SectionCard icon={MagnifyingGlass} title={t('opps.validate_now')} color="#2563EB">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {scan.research_hooks.map((r, i) => {
                    const q = encodeURIComponent(r.query_or_subreddit || '');
                    const url = r.platform?.toLowerCase().includes('reddit')
                      ? `https://www.reddit.com/search/?q=${q}`
                      : r.platform?.toLowerCase().includes('tiktok')
                      ? `https://www.tiktok.com/search?q=${q}`
                      : r.platform?.toLowerCase().includes('trends')
                      ? `https://trends.google.com/trends/explore?q=${q}`
                      : `https://www.google.com/search?q=${q}`;
                    return (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                        data-testid={`research-hook-${i}`}
                        className="p-3 bg-white/5 border border-white/10 rounded-lg flex items-center justify-between hover:border-[#2563EB]/50 transition group">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-[#2563EB] font-semibold mb-0.5">{r.platform}</div>
                          <div className="text-sm font-semibold">{r.query_or_subreddit}</div>
                        </div>
                        <ArrowRight size={14} className="text-white/40 group-hover:text-[#2563EB] transition" />
                      </a>
                    );
                  })}
                </div>
              </SectionCard>
            )}
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <SectionCard icon={Trophy} title={t('opps.history')} color="#666">
            <div className="space-y-2">
              {history.map((h) => (
                <div key={h.scan_id} className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg flex items-center justify-between gap-2">
                  <button data-testid={`history-item-${h.scan_id}`}
                    onClick={() => loadFromHistory(h.scan_id)}
                    className="flex-1 text-left hover:opacity-80 transition">
                    <div className="font-[Outfit] font-bold text-sm">{h.seed_niche}</div>
                    <div className="text-xs text-white/50 mt-0.5">
                      {h.target_market || 'global'} · {new Date(h.created_at).toLocaleDateString(i18n.language === 'pt' ? 'pt-PT' : 'en-US')}
                      {h.scan?.market_signals?.saturation_label && (
                        <> · {t('opps.sat_word')} <span className="capitalize" style={{ color: SATURATION_COLOR[h.scan.market_signals.saturation_label] }}>{h.scan.market_signals.saturation_label}</span></>
                      )}
                    </div>
                  </button>
                  <button data-testid={`history-delete-${h.scan_id}`}
                    onClick={() => deleteItem(h.scan_id)}
                    className="p-1.5 rounded text-white/40 hover:text-[#EF4444] hover:bg-[#EF4444]/10">
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {!result && history.length === 0 && (
          <div className="p-8 bg-[#121212] border border-dashed border-white/15 rounded-2xl text-center">
            <Compass size={32} weight="bold" className="text-[#A855F7] mx-auto mb-3" />
            <h3 className="font-[Outfit] font-bold text-lg mb-1">{t('opps.empty_title')}</h3>
            <p className="text-sm text-white/60 max-w-md mx-auto">{t('opps.empty_sub')}</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

